// src/pages/admin/AdminHolidays.jsx
// ============================================================
// Admin page to define and manage company holidays.
// Holidays automatically reflect in attendance — employees
// marked as "Holiday" instead of "Absent" on those days.
// ============================================================

import React, { useState, useMemo } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  useHolidays,
  createHoliday,
  updateHoliday,
  deleteHoliday,
} from "../../hooks/useFirestoreExtensions";
import { ADMIN_NAV } from "../../utils/navigation";
import Layout from "../../components/common/Layout";

const HOLIDAY_TYPES = [
  { value: "public", label: "Public Holiday", color: "green" },
  { value: "optional", label: "Optional Holiday", color: "green" },
  { value: "restricted", label: "Restricted Holiday", color: "green" },
];

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// ── Utility: today as YYYY-MM-DD ──────────────────────────
function getTodayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// ── Utility: tomorrow as YYYY-MM-DD (min allowed holiday date) ──
function getTomorrowStr() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// ── Utility: get current year ─────────────────────────────
const CURRENT_YEAR = new Date().getFullYear();

export default function AdminHolidays() {
  const { userProfile } = useAuth();
  const { data: holidays, loading } = useHolidays();

  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState({});
  const [error, setError] = useState("");

  // Filter state
  const [filterYear, setFilterYear] = useState(CURRENT_YEAR);
  const [filterMonth, setFilterMonth] = useState(0); // 0 = all months
  const [filterType, setFilterType] = useState("all");

  // Form state
  const emptyForm = { name: "", date: "", type: "public" };
  const [form, setForm] = useState(emptyForm);

  // Filtered list
  const filtered = useMemo(() => {
    return holidays.filter((h) => {
      const [y, m] = h.date.split("-").map(Number);
      if (y !== filterYear) return false;
      if (filterMonth && m !== filterMonth) return false;
      if (filterType !== "all" && h.type !== filterType) return false;
      return true;
    });
  }, [holidays, filterYear, filterMonth, filterType]);

  // Year options
  const yearOptions = useMemo(() => {
    const years = [];
    for (let y = CURRENT_YEAR + 1; y >= CURRENT_YEAR - 2; y--) years.push(y);
    return years;
  }, []);

  // Open add form
  function openAdd() {
    setEditItem(null);
    setForm(emptyForm);
    setError("");
    setShowForm(true);
  }

  // Open edit form
  function openEdit(holiday) {
    setEditItem(holiday);
    setForm({
      name: holiday.name,
      date: holiday.date,
      type: holiday.type || "public",
    });
    setError("");
    setShowForm(true);
  }

  // Submit
  async function handleSubmit() {
    if (!form.name.trim() || !form.date) {
      setError("Holiday name and date are required.");
      return;
    }
    
    if (!/^[A-Za-z ]+$/.test(form.name.trim())) {
      setError("❌ Holiday name can only contain letters and spaces.");
      return;
    }

    const today = getTodayStr();
    if (form.date <= today) {
      setError("❌ Holiday date must be at least 1 day ahead. Today and past dates are not allowed.");
      return;
    }

    const d = new Date(form.date);
    if (d.getDay() === 0) {
      setError("❌ Sundays cannot be selected as a Holiday Date.");
      return;
    }
    
    setSaving(true);
    setError("");
    try {
      if (editItem) {
        await updateHoliday(editItem.id, {
          name: form.name.trim(),
          date: form.date,
          type: form.type,
        }, userProfile);
      } else {
        await createHoliday({
          name: form.name.trim(),
          date: form.date,
          type: form.type,
          userProfile,
        });
      }
      setShowForm(false);
      setForm(emptyForm);
      setEditItem(null);
    } catch (err) {
      setError(err.message || "Failed to save holiday.");
    }
    setSaving(false);
  }

  // Delete
  async function handleDelete(id) {
    if (!window.confirm("Delete this holiday?")) return;
    setDeleting((d) => ({ ...d, [id]: true }));
    try {
      await deleteHoliday(id, userProfile);
    } catch (err) {
      console.error("[AdminHolidays] delete:", err);
    }
    setDeleting((d) => ({ ...d, [id]: false }));
  }

  // Stats
  const totalThisYear = holidays.filter((h) =>
    h.date.startsWith(String(filterYear)),
  ).length;
  const publicCount = holidays.filter(
    (h) => h.date.startsWith(String(filterYear)) && h.type === "public",
  ).length;

  return (
    <Layout navItems={ADMIN_NAV} panelLabel="Admin Panel">
      <div className="page-header">
        <div>
          <h1 className="page-title"> Holidays</h1>
        </div>
        <button className="btn-primary" onClick={openAdd}>
          + Add Holiday
        </button>
      </div>

      {/* KPI chips */}
      <div className="kpi-grid" style={{ marginBottom: "1.5rem" }}>
        <div className="kpi-card kpi-card--green">
          <div className="kpi-icon">
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="size-6"
    width="28"
    height="28"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
    />
  </svg>
</div>
          <div className="kpi-body">
            <span className="kpi-value">{totalThisYear}</span>
            <span className="kpi-label">Total {filterYear}</span>
          </div>
        </div>
        <div className="kpi-card kpi-card--blue">
          <div className="kpi-icon">
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="size-6"
    width="28"
    height="28"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5m-9-6h.008v.008H12v-.008ZM12 15h.008v.008H12V15Zm0 2.25h.008v.008H12v-.008ZM9.75 15h.008v.008H9.75V15Zm0 2.25h.008v.008H9.75v-.008ZM7.5 15h.008v.008H7.5V15Zm0 2.25h.008v.008H7.5v-.008Zm6.75-4.5h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V15Zm0 2.25h.008v.008h-.008v-.008Zm2.25-4.5h.008v.008H16.5v-.008Zm0 2.25h.008v.008H16.5V15Z"
    />
  </svg>
</div>
          <div className="kpi-body">
            <span className="kpi-value">{publicCount}</span>
            <span className="kpi-label">Public Holidays</span>
          </div>
        </div>
        <div className="kpi-card kpi-card--amber">
          <div className="kpi-icon">
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="size-6"
    width="28"
    height="28"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5m-9-6h.008v.008H12v-.008ZM12 15h.008v.008H12V15Zm0 2.25h.008v.008H12v-.008ZM9.75 15h.008v.008H9.75V15Zm0 2.25h.008v.008H9.75v-.008ZM7.5 15h.008v.008H7.5V15Zm0 2.25h.008v.008H7.5v-.008Zm6.75-4.5h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V15Zm0 2.25h.008v.008h-.008v-.008Zm2.25-4.5h.008v.008H16.5v-.008Zm0 2.25h.008v.008H16.5V15Z"
    />
  </svg>
</div>         
 <div className="kpi-body">
            <span className="kpi-value">{totalThisYear - publicCount}</span>
            <span className="kpi-label">Other Holidays</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="table-filters" style={{ marginBottom: "1rem" }}>
        <select
          className="filter-select"
          value={filterYear}
          onChange={(e) => setFilterYear(Number(e.target.value))}
        >
          {yearOptions.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>

        <select
          className="filter-select"
          value={filterMonth}
          onChange={(e) => setFilterMonth(Number(e.target.value))}
        >
          <option value={0}>All Months</option>
          {MONTHS.map((m, i) => (
            <option key={m} value={i + 1}>
              {m}
            </option>
          ))}
        </select>

        <select
          className="filter-select"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
        >
          <option value="all">All Types</option>
          {HOLIDAY_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      </div>

      {/* Add / Edit Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {editItem ? "Edit Holiday" : "Add Holiday"}
              </h2>
              <button
                className="modal-close"
                onClick={() => setShowForm(false)}
              >
                ✕
              </button>
            </div>

            <div className="form-group">
              <label className="form-label">Holiday Name *</label>
              <input
                className="form-input"
                placeholder="e.g. Republic Day, Diwali"
                value={form.name}
                onChange={(e) => {
                  // Strip special characters and numbers — allow only letters, spaces
                  const cleaned = e.target.value.replace(/[^A-Za-z ]/g, "");
                  setForm((f) => ({ ...f, name: cleaned }));
                }}
              />
              <p style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: "0.25rem" }}>
                📝 Only letters and spaces are allowed.
              </p>
            </div>

            <div className="form-group">
              <label className="form-label">Date *</label>
              <input
                type="date"
                className="form-input"
                value={form.date}
                min={getTomorrowStr()}
                onChange={(e) => {
                  const val = e.target.value;
                  if (!val) {
                    setForm((f) => ({ ...f, date: "" }));
                    return;
                  }
                  const d = new Date(val);
                  if (d.getDay() === 0) {
                    setError("❌ Sundays cannot be selected as a Holiday Date.");
                    setForm((f) => ({ ...f, date: "" }));
                  } else {
                    setError("");
                    setForm((f) => ({ ...f, date: val }));
                  }
                }}
              />
              <p style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: "0.25rem" }}>
                📅 Must be at least 1 day ahead. Today, past dates &amp; Sundays are not allowed.
              </p>
            </div>

            <div className="form-group">
              <label className="form-label">Holiday Type</label>
              <select
                className="form-select"
                value={form.type}
                onChange={(e) =>
                  setForm((f) => ({ ...f, type: e.target.value }))
                }
              >
                {HOLIDAY_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
              <p
                style={{
                  fontSize: "0.75rem",
                  color: "var(--text-muted)",
                  marginTop: "0.25rem",
                }}
              >
                Public: all employees off. Optional: employees may choose.
                Restricted: select employees.
              </p>
            </div>

            {error && <div className="form-error">{error}</div>}

            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => setShowForm(false)}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleSubmit}
                disabled={saving}
              >
                {saving
                  ? "Saving…"
                  : editItem
                    ? "Update Holiday"
                    : "Add Holiday"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="card-loading">Loading holidays…</div>
      ) : filtered.length === 0 ? (
        <div className="card-empty">
          No holidays found for the selected filters.
          <br />
          <button
            className="btn-primary"
            style={{ marginTop: "1rem" }}
            onClick={openAdd}
          >
            + Add First Holiday
          </button>
        </div>
      ) : (
        <div className="data-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Holiday Name</th>
                <th>Date</th>
                <th>Day</th>
                <th>Type</th>
                <th>Created / Modified By</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((h, idx) => {
                const dt = new Date(h.date + "T12:00:00");
                const dayName = dt.toLocaleDateString("en-IN", {
                  weekday: "long",
                });
                const dateStr = dt.toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                });
                const typeInfo =
                  HOLIDAY_TYPES.find((t) => t.value === h.type) ||
                  HOLIDAY_TYPES[0];
                const isPast = h.date < getTodayStr();

                return (
                  <tr key={h.id} style={{ opacity: isPast ? 0.75 : 1 }}>
                    <td
                      style={{ color: "var(--text-muted)", fontSize: "0.8rem" }}
                    >
                      {idx + 1}
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{h.name}</div>
                      {isPast && (
                        <div
                          style={{
                            fontSize: "0.7rem",
                            color: "var(--text-muted)",
                          }}
                        >
                          Past
                        </div>
                      )}
                    </td>
                    <td
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: "0.85rem",
                      }}
                    >
                      {dateStr}
                    </td>
                    <td style={{ color: "var(--text-secondary)" }}>
                      {dayName}
                    </td>
                    <td>
                      <span
                        className={`status-pill status-pill--${typeInfo.color}`}
                      >
                        {typeInfo.label}
                      </span>
                    </td>
                    <td>
                      <div style={{ fontSize: "0.78rem", fontWeight: 600, color: "var(--text-primary)" }}>
                        {h.updatedByName || h.createdByName || "Admin"}
                      </div>
                      <div style={{ fontSize: "0.68rem", color: "var(--text-muted)", textTransform: "uppercase" }}>
                        Role: {h.updatedByRole || h.createdByRole || "Admin"}
                      </div>
                    </td>
                    <td>
                      <div className="action-btns">
                        <button
  className="btn-secondary-sm"
  onClick={() => openEdit(h)}
>
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="size-6"
    width="18"
    height="18"
    style={{ marginRight: "4px", verticalAlign: "middle" }}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"
    />
  </svg>
  Edit
</button>
                        <button
                          className="btn-danger-sm"
                          onClick={() => handleDelete(h.id)}
                          disabled={deleting[h.id]}
                        >
                          {
  deleting[h.id] ? (
    "…"
  ) : (
    <>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="size-6"
        width="18"
        height="18"
        style={{ marginRight: "4px", verticalAlign: "middle" }}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
        />
      </svg>
      Delete
    </>
  )
}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Upcoming holidays preview */}
      {!loading && filtered.length > 0 && (
        <div style={{ marginTop: "2rem" }}>
          <h3
            style={{
              fontSize: "0.9rem",
              fontWeight: 700,
              color: "var(--text-secondary)",
              marginBottom: "0.75rem",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Upcoming Holidays
          </h3>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem" }}>
            {holidays
              .filter((h) => h.date >= getTodayStr())
              .slice(0, 6)
              .map((h) => {
                const dt = new Date(h.date + "T12:00:00");
                const typeInfo =
                  HOLIDAY_TYPES.find((t) => t.value === h.type) ||
                  HOLIDAY_TYPES[0];
                return (
                  <div
                    key={h.id}
                    style={{
                      background: "var(--surface)",
                      border: "1px solid var(--border)",
                      borderRadius: "var(--radius)",
                      padding: "0.75rem 1rem",
                      minWidth: "160px",
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: "0.9rem" }}>
                      {h.name}
                    </div>
                    <div
                      style={{
                        fontSize: "0.75rem",
                        color: "var(--text-muted)",
                        marginTop: "0.25rem",
                      }}
                    >
                      {dt.toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "short",
                      })}
                    </div>
                    <span
                      className={`status-pill status-pill--${typeInfo.color}`}
                      style={{ marginTop: "0.4rem", display: "inline-block" }}
                    >
                      {typeInfo.label}
                    </span>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </Layout>
  );
}
